import { loadVenuePlanFromVenueLayoutId } from '../venue';
import { graphql } from '../../generated';
import { apollo } from '../graphql';
import { indexBy } from '../../util/collections';
import { Signal } from '../signal';
import { Selection } from './selection';
import { Seat, Section, VenueLayout, PricingCategory } from './types';
import { blockSeats, loadAllocations, loadSeatStatus, unblockSeats } from '../contingent';
import { compact } from 'redux-database/dist/util';
import { boundsOf, pricingCategoryColors } from './helper';
export class Seatmap {
  layout: VenueLayout;
  structureEditable = false;
  allocationEditable = false;
  readonly signal = new Signal();
  readonly selection = new Selection(this);
  private readonly eventId?: string;

  private _loading = true;

  constructor({ id, eventId }: { id: string; eventId?: string }) {
    this.eventId = eventId;
    this.layout = {
      id,
      sections: [],
      pricingCategories: [],
      boundingBox: { x: 0, y: 0, width: 100, height: 100 },
      graphics: [],
    };
    this.selection.signal.notify(this.signal);
    this.reload().then(() => {
      if (this.eventId) {
        Promise.all([this.reloadSeatStatus(), this.reloadAllocation(), this.reloadEventPermissions()]).then(() => {
          this.loading = false;
        });
      } else {
        this.loading = false;
      }
    });
  }

  get loading(): boolean {
    return this._loading;
  }

  private set loading(loading: boolean) {
    this._loading = loading;
    this.signal.emit();
  }

  getPricingCategoryName(id: string): string {
    return this.layout.pricingCategories.find((category) => category.id === id)?.name || 'undefined';
  }

  async reload(): Promise<void> {
    const plan = await loadVenuePlanFromVenueLayoutId(this.layout.id);
    const { data } = await apollo.query({
      query: SEATMAP,
      variables: { id: this.layout.id },
    });
    if (!data.venueLayout) throw new Error('No data');

    const pricingCategoriesById = indexBy(
      data.venueLayout.pricingCategories.map((e, i) => ({
        ...e,
        color: pricingCategoryColors[i % pricingCategoryColors.length],
      })),
      'id'
    );
    const sections: Section[] = plan.sections.map((section) => {
      const pricingCategory = (section.pricingCategoryId && pricingCategoriesById[section.pricingCategoryId]) || null;
      return {
        id: section.id,
        name: section.name,
        hullPoints: section.hullPoints,
        generalAdmission: section.generalAdmission,
        pricingCategory,
        seats: [],
        hash: '', // will be overwritten on once seats are loaded
      };
    });
    const sectionsById = indexBy(sections, 'id');
    plan.sections.forEach((planSection) => {
      const section = sectionsById[planSection.id];
      planSection.seats.forEach((seat) => {
        section.seats.push({
          id: seat.id,
          x: seat.x,
          y: seat.y,
          row: seat.row,
          name: seat.name,
          section,
          allocation: null,
          pricingCategory: pricingCategoriesById[seat.pricingCategoryId],
        });
      });
      section.hash = this.sectionHash(section);
    });
    this.layout.sections = sections;
    this.layout.graphics = plan.graphics.map((graphic) => ({
      id: graphic.id,
      name: graphic.name,
      points: graphic.points,
      center: graphic.center,
      imageUrl: graphic.imageUrl,
      boundingBox: boundsOf(graphic.points),
    }));
    this.layout.boundingBox = boundsOf(
      plan.sections.flatMap((section) => section.hullPoints).concat(plan.graphics.flatMap((g) => g.points))
    );
    this.layout.pricingCategories = Object.values(pricingCategoriesById);
    this.structureEditable = data.venueLayout.permissions.update;
    this.signal.emit();
  }

  async reloadSeatStatus(): Promise<void> {
    if (!this.eventId) return;
    const status = await loadSeatStatus({ id: this.eventId });
    const seatsById = indexBy(
      this.layout.sections.flatMap((section) => section.seats),
      'id'
    );
    Object.values(status).forEach((item) => {
      const seat = seatsById[item.seatId];
      if (!seat) return;
      seat.allocation = {
        status: item.status,
        blockReason: item.blockReason,
        permanent: !!item.permanent,
        itemId: item.id,
      };
    });
    this.layout.sections.forEach((section) => {
      section.hash = this.sectionHash(section);
    });
    this.signal.emit();
  }

  pollSeatStatus(): (() => void) | undefined {
    if (!this.eventId) return;
    const state = { cancelled: false };
    const poll = async (): Promise<void> => {
      if (state.cancelled) return;
      if (!this.loading) {
        await this.reloadSeatStatus();
      }
      setTimeout(poll, 30_000);
    };
    poll();
    return () => {
      state.cancelled = true;
    };
  }

  async reloadAllocation(): Promise<void> {
    const id = this.eventId;
    if (!id) return;
    const allocations = await loadAllocations({ id }, { updateGeneralAdmissionOnly: true });
    const sectionsById = indexBy(this.layout.sections, 'id');
    allocations.forEach((allocation) => {
      const section = sectionsById[allocation.section.id];
      if (!section) return;
      section.allocation = allocation;
      section.hash = this.sectionHash(section);
    });
    this.signal.emit();
  }

  async reloadEventPermissions(): Promise<void> {
    const id = this.eventId;
    if (!id) return;
    const { data } = await apollo.query({ query: EVENT_PERMISSIONS, variables: { id } });
    this.allocationEditable = !!data.event?.permissions.update;

    this.signal.emit();
  }

  async changeSelectionPricingCategory(pricingCategory: PricingCategory): Promise<void> {
    const section = this.selection.section;
    const seats = Array.from(this.selection.seats);
    if (section) {
      await apollo.mutate({
        mutation: UPDATE_VENUE_SECTION_PRICING_CATEGORIES,
        variables: { sectionId: section.id, pricingCategoryId: pricingCategory.id },
      });
      section.pricingCategory = pricingCategory;
      section.hash = this.sectionHash(section);
      this.signal.emit();
    } else if (seats) {
      await apollo.mutate({
        mutation: UPDATE_VENUE_SEAT_PRICING_CATEGORIES,
        variables: { seatIds: seats.map((e) => e.id), pricingCategoryId: pricingCategory.id },
      });
      seats.forEach((e) => {
        e.pricingCategory = pricingCategory;
        e.section.hash = this.sectionHash(e.section);
      });
      this.signal.emit();
    }
  }

  async blockSeats({ seats, reason, permanent }: { seats: Seat[]; reason: string; permanent: boolean }): Promise<void> {
    if (!this.eventId) return;
    const itemsIds = compact(seats.map((e) => e.allocation?.itemId));
    await blockSeats({ eventId: this.eventId, itemsIds, reason, permanent });
    seats.forEach((e) => {
      if (!e.allocation) return;
      e.allocation.blockReason = reason;
      e.allocation.permanent = permanent;
      e.allocation.status = 'blocked';
      e.section.hash = this.sectionHash(e.section);
    });
    this.signal.emit();
  }

  async unblockSeats(seats: Seat[]): Promise<void> {
    if (!this.eventId) return;
    const itemsIds = compact(seats.filter((e) => e.allocation?.status === 'blocked').map((e) => e.allocation?.itemId));
    await unblockSeats({ eventId: this.eventId, itemsIds });
    seats.forEach((e) => {
      if (!e.allocation) return;
      e.allocation.blockReason = undefined;
      e.allocation.permanent = false;
      e.allocation.status = 'available';
      e.section.hash = this.sectionHash(e.section);
    });
    this.signal.emit();
  }

  private seatHash(seat: Seat): string {
    return seat.id + seat.pricingCategory?.id + JSON.stringify(seat.allocation);
  }

  private sectionHash(section: Section): string {
    return (
      section.seats.map((e) => this.seatHash(e)).join('') +
      section.pricingCategory?.id +
      JSON.stringify(section.allocation)
    );
  }
}

const UPDATE_VENUE_SEAT_PRICING_CATEGORIES = graphql(`
  mutation UpdateVenueSeatPricingCategories($seatIds: [ID!]!, $pricingCategoryId: ID!) {
    updateVenueSeatPricingCategories(input: { seatIds: $seatIds, pricingCategoryId: $pricingCategoryId }) {
      clientMutationId
    }
  }
`);

const UPDATE_VENUE_SECTION_PRICING_CATEGORIES = graphql(`
  mutation UpdateVenueSectionPricingCategories($sectionId: ID!, $pricingCategoryId: ID!) {
    updateVenueSectionPricingCategories(input: { sectionId: $sectionId, pricingCategoryId: $pricingCategoryId }) {
      clientMutationId
    }
  }
`);

const SEATMAP = graphql(`
  query Seatmap($id: ID!) {
    venueLayout(id: $id) {
      id
      permissions {
        id
        update
      }
      pricingCategories {
        id
        name
      }
    }
  }
`);

const EVENT_PERMISSIONS = graphql(`
  query EventPermissions($id: ID!) {
    event(id: $id) {
      id
      permissions {
        id
        update
      }
    }
  }
`);
